import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import BadgeIcon from '@mui/icons-material/Badge'

import { employeeNameOf, invalidRestDayText } from '../services/invalidRestDay'

/**
 * #542: what a rest day the engine cannot read looks like now.
 *
 * Deliberately NOT the error screen. Nothing broke — the engine refused on
 * purpose (#505), because guessing a week boundary is a wrong wage that looks
 * finished. The employer got that refusal as "אירעה שגיאה בלתי צפויה" plus
 * `restDay "0" אינו יום מנוחה שבועי`, and the two ways out it offered ("נסה
 * שוב", "חזרה לדף הבית") both lead back to the same screen.
 *
 * The single thing that fixes it is one field on the employee card, so that is
 * what this says, and it links there. The wording is Shai's decision on #542.
 */

/**
 * The employee card behind whatever screen we are on.
 *
 * Every screen that prices an employee lives under
 * `/employers/:employerId/calculations/:calculationId/…`, and the error
 * boundary — which sits above the routes and matches none of them — cannot read
 * those params. Reading them off the path is what lets the link work there too;
 * a screen that knows the ids passes them in instead.
 */
export const employeeCardPathFrom = pathname =>
  /^\/employers\/([^/]+)\/calculations\/([^/]+)/.exec(pathname || '')?.[0] ?? null

export default function InvalidRestDay({ error, employerId, calculationId }) {
  const { pathname } = useLocation()
  const to = employerId && calculationId
    ? `/employers/${employerId}/calculations/${calculationId}`
    : employeeCardPathFrom(pathname)

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        padding: 3,
      }}
      data-testid="invalid-rest-day"
    >
      <Paper elevation={3} sx={{ padding: 4, textAlign: 'center', maxWidth: 500 }}>
        <BadgeIcon color="disabled" sx={{ fontSize: 64, marginBottom: 2 }} />
        <Typography variant="h6" gutterBottom>
          {invalidRestDayText(employeeNameOf(error))}
        </Typography>
        {/* No "נסה שוב" and no "לדף הבית": neither changes the field, and
            offering them is what made the old screen a dead end. */}
        {to && (
          <Box sx={{ marginTop: 3 }}>
            <Button variant="contained" component={Link} to={to} data-testid="invalid-rest-day-card">
              פתיחת כרטיס העובד
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  )
}
